import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import DomainRoundedIcon from "@mui/icons-material/DomainRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { getErrorMessage, getRequest, PROJECTS_API } from "./api";
import { panelSx, statusChip } from "./ProjectsCommon";

const statusOrder = [
  "PLANNED",
  "READY",
  "IN_PROGRESS",
  "LIVE",
  "BLOCKED",
  "COMPLETED",
];

const scheduleTone = {
  COMPLETED: {
    label: "Completed",
    color: "#0369A1",
    bg: "rgba(14,165,233,0.14)",
  },
  OVERDUE: { label: "Overdue", color: "#B91C1C", bg: "rgba(239,68,68,0.14)" },
  DUE_SOON: {
    label: "Due Soon",
    color: "#92400E",
    bg: "rgba(245,158,11,0.16)",
  },
  ON_TRACK: { label: "On Track", color: "#166534", bg: "rgba(22,163,74,0.14)" },
  NO_TARGET_DATE: {
    label: "No Target Date",
    color: "#475569",
    bg: "rgba(100,116,139,0.12)",
  },
};

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function MetricCard({ icon, label, value, caption, tone = "#0F172A" }) {
  return (
    <Card elevation={0} sx={{ ...panelSx, height: "100%" }}>
      <CardContent sx={{ p: 2.25 }}>
        <Stack direction="row" spacing={1.4} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              color: tone,
              backgroundColor: "rgba(15,23,42,0.05)",
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 900,
                color: "#64748B",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontSize: 28,
                fontWeight: 900,
                color: "#0F172A",
                letterSpacing: "-0.04em",
              }}
            >
              {value}
            </Typography>
          </Box>
        </Stack>
        {caption ? (
          <Typography
            sx={{ mt: 1.5, color: "#64748B", fontSize: 13, lineHeight: 1.55 }}
          >
            {caption}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ProjectDetailSkeleton() {
  const metricSkeletons = Array.from({ length: 4 });
  const tableRows = Array.from({ length: 5 });
  const detailRows = Array.from({ length: 3 });

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100%",
        background: "linear-gradient(180deg, #F3F6FA 0%, #EDF2F7 100%)",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.4, md: 3 },
          mb: 3,
          borderRadius: 4,
          color: "#F8FAFC",
          background: "linear-gradient(120deg, #0F172A 0%, #1E3A8A 58%, #0F766E 100%)",
          boxShadow: "0 18px 38px rgba(15,23,42,0.12)",
        }}
      >
        <Skeleton variant="rounded" width={142} height={36} sx={{ bgcolor: "rgba(255,255,255,0.18)", mb: 2 }} />
        <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" spacing={2}>
          <Box sx={{ maxWidth: 900, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Skeleton variant="rounded" width={140} height={28} sx={{ bgcolor: "rgba(255,255,255,0.18)", borderRadius: 999 }} />
              <Skeleton variant="rounded" width={92} height={28} sx={{ bgcolor: "rgba(255,255,255,0.18)", borderRadius: 999 }} />
              <Skeleton variant="rounded" width={110} height={28} sx={{ bgcolor: "rgba(255,255,255,0.18)", borderRadius: 999 }} />
            </Stack>
            <Skeleton variant="text" width="58%" height={58} sx={{ mt: 1.4, bgcolor: "rgba(255,255,255,0.18)" }} />
            <Skeleton variant="text" width="46%" height={26} sx={{ bgcolor: "rgba(255,255,255,0.18)" }} />
            <Skeleton variant="text" width="72%" height={24} sx={{ mt: 1.1, bgcolor: "rgba(255,255,255,0.18)" }} />
          </Box>
          <Box
            sx={{
              minWidth: { lg: 320 },
              p: 2,
              borderRadius: 3,
              backgroundColor: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <Skeleton variant="text" width="56%" height={20} sx={{ bgcolor: "rgba(255,255,255,0.18)" }} />
            <Skeleton variant="text" width="42%" height={58} sx={{ mt: 1, bgcolor: "rgba(255,255,255,0.18)" }} />
            <Skeleton variant="rounded" width="100%" height={10} sx={{ mt: 1.4, borderRadius: 999, bgcolor: "rgba(255,255,255,0.18)" }} />
            <Skeleton variant="text" width="74%" height={22} sx={{ mt: 1.4, bgcolor: "rgba(255,255,255,0.18)" }} />
          </Box>
        </Stack>
      </Paper>

      <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
        <Box sx={{ width: "100%", display: "flex", flexWrap: "wrap", gap: 2 }}>
          {metricSkeletons.map((_, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 16px)",
                  md: "1 1 calc(25% - 16px)",
                },
              }}
            >
              <Card elevation={0} sx={{ ...panelSx, width: "100%", height: "100%", p: 2, borderRadius: 3 }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2 }} />
                  <Skeleton variant="text" width="42%" height={18} />
                </Stack>
                <Skeleton variant="text" width="28%" height={40} sx={{ mt: 1 }} />
                <Skeleton variant="text" width="72%" height={20} />
              </Card>
            </Box>
          ))}
        </Box>

        <Paper elevation={0} sx={{ ...panelSx, p: 2.4 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="28%" height={32} />
              <Skeleton variant="text" width="54%" height={22} />
            </Box>
            <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 2 }} />
          </Stack>
          <Stack spacing={1.2} sx={{ mt: 2 }}>
            {tableRows.map((_, index) => (
              <Box key={index} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr 1fr 1fr 1fr 0.8fr" }, gap: 1.5, alignItems: "center" }}>
                <Skeleton variant="text" height={24} />
                <Skeleton variant="text" height={24} />
                <Skeleton variant="text" height={24} />
                <Skeleton variant="text" height={24} />
                <Skeleton variant="rounded" height={8} sx={{ borderRadius: 999 }} />
                <Skeleton variant="rounded" height={26} sx={{ borderRadius: 999 }} />
              </Box>
            ))}
          </Stack>
        </Paper>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", "& > *": { flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" } } }}>
          {[0, 1].map((panel) => (
            <Paper key={panel} elevation={0} sx={{ ...panelSx, p: 2.4 }}>
              <Skeleton variant="text" width="36%" height={30} />
              <Divider sx={{ my: 1.6 }} />
              <Stack spacing={1.35}>
                {detailRows.map((_, index) => (
                  <Stack key={index} direction="row" justifyContent="space-between" alignItems="center" spacing={1.2}>
                    <Skeleton variant="text" width="42%" height={24} />
                    <Skeleton variant="text" width="18%" height={24} />
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default function ProjectDetailDashboard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadProject = useCallback(async () => {
    if (!token || !activeOrg?.id || !projectId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getRequest(
        `${PROJECTS_API}/deployments/${projectId}`,
        token,
        activeOrg,
      );
      setProject(data?.project || null);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load project dashboard"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg, projectId]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const dashboard = project?.dashboard || {};
  const progress = dashboard.progress_percent || 0;
  const sites = Array.isArray(project?.sites) ? project.sites : [];
  const schedule =
    scheduleTone[dashboard.schedule_state] || scheduleTone.NO_TARGET_DATE;

  const statusRows = useMemo(() => {
    const breakdown = dashboard.status_breakdown || {};
    const keys = Array.from(
      new Set([...statusOrder, ...Object.keys(breakdown)]),
    );
    return keys
      .map((key) => ({ status: key, count: breakdown[key] || 0 }))
      .filter((row) => row.count > 0);
  }, [dashboard.status_breakdown]);

  if (loading) {
    return <ProjectDetailSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Project not found.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100%",
        background: "linear-gradient(180deg, #F3F6FA 0%, #EDF2F7 100%)",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.4, md: 3 },
          mb: 3,
          borderRadius: 4,
          color: "#F8FAFC",
         background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            boxShadow: "0 30px 80px rgba(15, 23, 42, 0.18)",
        }}
      >
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate("/projects/deployments")}
          sx={{
            mb: 2,
            color: "#DBEAFE",
            fontWeight: 800,
            textTransform: "none",
          }}
        >
          Back to Projects
        </Button>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box sx={{ maxWidth: 900 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Chip
                label="Project Dashboard"
                sx={{
                  color: "#DBEAFE",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  fontWeight: 800,
                }}
              />
              {statusChip(project.status)}
              <Chip
                label={schedule.label}
                sx={{
                  color: schedule.color,
                  backgroundColor: schedule.bg,
                  fontWeight: 800,
                }}
              />
            </Stack>
            <Typography
              sx={{
                mt: 1.4,
                fontSize: { xs: 28, md: 40 },
                fontWeight: 600,
                letterSpacing: "-0.055em",
                lineHeight: 1,
              }}
            >
              {project.name}
            </Typography>
            <Typography
              sx={{
                mt: 1.2,
                color: "rgba(226,232,240,0.84)",
                fontSize: 15.5,
                lineHeight: 1.65,
              }}
            >
              {project.customer?.name || "Customer pending"} / {project.code} /{" "}
              {project.project_type || "Deployment Program"}
            </Typography>
            {project.description ? (
              <Typography
                sx={{
                  mt: 1.1,
                  color: "rgba(226,232,240,0.78)",
                  maxWidth: 820,
                  lineHeight: 1.7,
                }}
              >
                {project.description}
              </Typography>
            ) : null}
          </Box>
          <Box
            sx={{
              minWidth: { lg: 320 },
              p: 2,
              borderRadius: 3,
              backgroundColor: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "#BFDBFE",
                fontWeight: 900,
              }}
            >
              Rollout Completion
            </Typography>
            <Stack
              direction="row"
              alignItems="baseline"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <Typography
                sx={{ fontSize: 44, fontWeight: 950, letterSpacing: "-0.06em" }}
              >
                {progress}%
              </Typography>
              <Typography sx={{ color: "rgba(226,232,240,0.8)" }}>
                complete
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                mt: 1.4,
                height: 10,
                borderRadius: 999,
                backgroundColor: "rgba(255,255,255,0.16)",
                "& .MuiLinearProgress-bar": { backgroundColor: "#22C55E" },
              }}
            />
            <Typography
              sx={{ mt: 1.4, color: "rgba(226,232,240,0.86)", fontSize: 13 }}
            >
              {dashboard.commissioned_devices || 0} commissioned /{" "}
              {dashboard.target_devices || 0} planned devices
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: "column",
        }}
      >
       <Box
  sx={{
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
  }}
>
  {[
    [
      <Inventory2RoundedIcon />,
      "Target Devices",
      dashboard.target_devices,
      `${dashboard.remaining_devices || 0} devices remaining`,
      "#2563EB",
    ],
    [
      <TaskAltRoundedIcon />,
      "Commissioned",
      dashboard.commissioned_devices,
      `${progress}% rollout completion`,
      "#16A34A",
    ],
    [
      <LocationOnRoundedIcon />,
      "Sites",
      dashboard.site_count,
      `${dashboard.ready_sites || 0} ready or in execution`,
      "#0F766E",
    ],
    [
      <WarningAmberRoundedIcon />,
      "Blocked Sites",
      dashboard.blocked_sites,
      "Sites needing operational attention",
      "#DC2626",
    ],
  ].map(([icon, label, value, caption, tone], i) => (
    <Box
      key={i}
      sx={{
        display: "flex",
        flex: {
          xs: "1 1 100%",              // ✅ 1 per row (mobile)
          sm: "1 1 calc(50% - 16px)", // 2 per row
          md: "1 1 calc(25% - 16px)", // 4 per row
        },
      }}
    >
      <Card
        elevation={0}
        sx={{
          ...panelSx,
          width: "100%",
          height: "100%",
          p: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          borderRadius: 3,
        }}
      >
        {/* Top */}
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: `${tone}15`,
              color: tone,
            }}
          >
            {icon}
          </Box>

          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: "#64748B",
              letterSpacing: "0.08em",
            }}
          >
            {label}
          </Typography>
        </Stack>

        {/* Middle */}
        <Typography
          sx={{
            fontSize: 30,
            fontWeight: 900,
            color: "#0F172A",
            lineHeight: 1.2,
            mt: 1,
          }}
        >
          {value || 0}
        </Typography>

        {/* Bottom */}
        <Typography
          sx={{
            fontSize: 13,
            color: "#64748B",
          }}
        >
          {caption}
        </Typography>
      </Card>
    </Box>
  ))}
</Box>

        <Grid item xs={12} lg={8}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2.4 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box>
                <Typography
                  sx={{ fontSize: 20, fontWeight: 900, color: "#0F172A" }}
                >
                  Site Readiness
                </Typography>
                <Typography sx={{ mt: 0.5, color: "#64748B", fontSize: 13.5 }}>
                  Field locations linked to this deployment program.
                </Typography>
              </Box>
              <Button
                variant="outlined"  
                onClick={() =>
                  navigate(`/projects/sites?project_id=${project.id}`,
                    {
                       state: {
                      fromProjectDetail: true,
                      projectName: project.name,
                    },
                    }
                  )
                }
                sx={{ fontWeight: 800 }}
              >
                Manage Sites
              </Button>
            </Stack>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900 }}>Site</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Owner</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Devices</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sites.map((site) => {
                    const siteProgress = site.summary?.progress_percent || 0;
                    return (
                      <TableRow
                        key={site.id}
                        hover
                        onClick={() =>
                          navigate(
                            `/projects/sites?project_id=${encodeURIComponent(
                              project.id,
                            )}&site_id=${encodeURIComponent(site.id)}`,
                          )
                        }
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "#F8FAFC",
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{ fontWeight: 800, color: "#0F172A" }}
                          >
                            {site.name}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                            {site.code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {[site.city, site.state].filter(Boolean).join(", ") ||
                            "-"}
                        </TableCell>
                        <TableCell>{site.site_owner || "-"}</TableCell>
                        <TableCell>
                          {site.commissioned_devices || 0} /{" "}
                          {site.planned_devices || 0}
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <LinearProgress
                              variant="determinate"
                              value={siteProgress}
                              sx={{ flex: 1, height: 7, borderRadius: 99 }}
                            />
                            <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                              {siteProgress}%
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{statusChip(site.status)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!sites.length ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography
                          sx={{ py: 4, textAlign: "center", color: "#64748B" }}
                        >
                          No sites are linked to this project yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Box>
          {/* Top 2 cards */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              "& > *": {
                flex: {
                  xs: "1 1 100%",
                  md: "1 1 calc(50% - 8px)",
                },
              },
            }}
          >
            {/* Program Control */}
            <Paper elevation={0} sx={{ ...panelSx, p: 2.4 }}>
              <Typography
                sx={{ fontSize: 18, fontWeight: 900, color: "#0F172A" }}
              >
                Program Control
              </Typography>
              <Divider sx={{ my: 1.6 }} />

              <Stack spacing={1.35}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <CalendarMonthRoundedIcon sx={{ color: "#2563EB" }} />
                  <Box>
                    <Typography
                      sx={{ fontSize: 12, color: "#64748B", fontWeight: 800 }}
                    >
                      Start Date
                    </Typography>
                    <Typography sx={{ fontWeight: 800 }}>
                      {formatDate(project.start_date)}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.2} alignItems="center">
                  <CalendarMonthRoundedIcon sx={{ color: "#0F766E" }} />
                  <Box>
                    <Typography
                      sx={{ fontSize: 12, color: "#64748B", fontWeight: 800 }}
                    >
                      Target End
                    </Typography>
                    <Typography sx={{ fontWeight: 800 }}>
                      {formatDate(project.target_end_date)}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.2} alignItems="center">
                  <DomainRoundedIcon sx={{ color: "#7C3AED" }} />
                  <Box>
                    <Typography
                      sx={{ fontSize: 12, color: "#64748B", fontWeight: 800 }}
                    >
                      Project Manager
                    </Typography>
                    <Typography sx={{ fontWeight: 800 }}>
                      {project.project_manager || "-"}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            {/* Status Breakdown */}
            <Paper elevation={0} sx={{ ...panelSx, p: 2.4 }}>
              <Typography
                sx={{ fontSize: 18, fontWeight: 900, color: "#0F172A" }}
              >
                Status Breakdown
              </Typography>
              <Divider sx={{ my: 1.6 }} />

              <Stack spacing={1.1}>
                {statusRows.map((row) => (
                  <Stack
                    key={row.status}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {statusChip(row.status)}
                    <Typography sx={{ fontWeight: 900, color: "#0F172A" }}>
                      {row.count}
                    </Typography>
                  </Stack>
                ))}

                {!statusRows.length && (
                  <Typography sx={{ color: "#64748B" }}>
                    No site status data yet.
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Box>

          {/* Bottom full-width card */}
          {project.notes && (
            <Paper elevation={0} sx={{ ...panelSx, p: 2.4, mt: 2 }}>
              <Typography
                sx={{ fontSize: 18, fontWeight: 900, color: "#0F172A" }}
              >
                Operational Notes
              </Typography>
              <Typography sx={{ mt: 1.2, color: "#475569", lineHeight: 1.7 }}>
                {project.notes}
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}
